import { cn } from '@/lib/utils';
import {
  Bot,
  FileText,
  Globe,
  LucideIcon,
  Network,
  ShieldCheck,
  TerminalSquare
} from 'lucide-react';

import { Mono } from './loginContent';

export const ENGAGEMENT_PIPELINE: {
  step: string;
  title: string;
  body: string;
  human?: boolean;
}[] = [
  {
    step: '01',
    title: 'Recon',
    body: 'Sweep the range, fingerprint hosts and services.'
  },
  {
    step: '02',
    title: 'Plan',
    body: 'Agents build a task tree of ATT&CK-mapped courses of action.'
  },
  {
    step: '03',
    title: 'Approve',
    body: 'Every command waits for your allow, edit, or skip.',
    human: true
  },
  {
    step: '04',
    title: 'Execute',
    body: 'Runs in your isolated Kali container, footprint tracked.'
  },
  {
    step: '05',
    title: 'Report',
    body: 'RMF, CMMC and PTES deliverables from the engagement ledger.'
  }
];

const APP_FEATURES: { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: Bot,
    title: 'Multi-agent orchestration',
    body: 'An orchestrator routes work to recon, task-tree, guide and executor agents.'
  },
  {
    icon: ShieldCheck,
    title: 'Human-in-the-loop control',
    body: 'Nothing touches a target until you approve the command.'
  },
  {
    icon: TerminalSquare,
    title: 'Isolated Kali sessions',
    body: 'Per-user containers over SSH keep every operation contained.'
  },
  {
    icon: Network,
    title: 'Live threat graphs',
    body: 'STIX 2.1, COA and operation timelines you can replay.'
  },
  {
    icon: Globe,
    title: 'Threat intel, cited',
    body: 'Grounded briefs from your knowledge base, the web and CTI feeds.'
  },
  {
    icon: FileText,
    title: 'Engagement reports',
    body: 'Control-mapped RMF, CMMC, NIST 800-53 and PTES deliverables.'
  }
];

export const APP_STATS = [
  { value: '8', label: 'Agents' },
  { value: '29', label: 'Tools' },
  { value: 'STIX 2.1', label: 'Graph model' },
  { value: 'Per-user', label: 'Kali sessions' }
];

export function FeatureGrid({ className }: { className?: string }) {
  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {APP_FEATURES.map(({ icon: Icon, title, body }) => (
        <div
          key={title}
          className="rounded-xl border border-border/60 bg-card/50 p-5"
        >
          <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg border border-primary/25 bg-primary/10 text-primary">
            <Icon className="h-4 w-4" />
          </span>
          <div className="text-sm font-semibold text-foreground">{title}</div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {body}
          </p>
        </div>
      ))}
    </div>
  );
}

export function PipelineSteps({ className }: { className?: string }) {
  return (
    <ol className={cn('grid gap-3 sm:grid-cols-2 lg:grid-cols-5', className)}>
      {ENGAGEMENT_PIPELINE.map(({ step, title, body, human }) => (
        <li
          key={step}
          className={cn(
            'rounded-xl border p-4',
            human
              ? 'border-primary/50 bg-primary/10'
              : 'border-border/60 bg-card/40'
          )}
        >
          <div className="flex items-center justify-between">
            <Mono className="text-[10px] text-muted-foreground">{step}</Mono>
            {human ? (
              <Mono className="text-[9px] text-primary">You</Mono>
            ) : null}
          </div>
          <div className="mt-2 text-sm font-semibold text-foreground">
            {title}
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {body}
          </p>
        </li>
      ))}
    </ol>
  );
}

export function StatBand({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/60 sm:grid-cols-4',
        className
      )}
    >
      {APP_STATS.map(({ value, label }) => (
        <div key={label} className="bg-card/60 px-5 py-4">
          <div className="text-xl font-bold text-foreground">{value}</div>
          <Mono className="mt-1 block text-[10px] text-muted-foreground">
            {label}
          </Mono>
        </div>
      ))}
    </div>
  );
}

export function CommandApprovalCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border/70 bg-card/80 p-4 shadow-lg shadow-black/30',
        className
      )}
    >
      <div className="flex items-center justify-between">
        <Mono className="text-[10px] text-muted-foreground">
          Command review
        </Mono>
        <Mono className="rounded-full border border-term-green/40 px-2 py-0.5 text-[10px] text-term-green">
          Risk 3/10
        </Mono>
      </div>
      <div
        className="mt-3 rounded-lg border border-border/60 bg-black/40 px-3 py-2 text-[13px] text-foreground"
        style={{ fontFamily: 'var(--font-mono)' }}
      >
        <span className="text-primary/70">$ </span>nmap -sV -sC 10.0.0.5
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Service and default-script scan of the promoted target.
      </p>
      <div className="mt-3 flex gap-2">
        <span className="flex-1 rounded-md bg-primary py-1.5 text-center text-xs font-medium text-primary-foreground">
          Allow
        </span>
        <span className="rounded-md border border-border/70 px-3 py-1.5 text-center text-xs text-foreground">
          Edit
        </span>
        <span className="rounded-md px-3 py-1.5 text-center text-xs text-muted-foreground">
          Skip
        </span>
      </div>
    </div>
  );
}

export function ChatThreadMock({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-3', className)}>
      <div className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-primary/15 px-3 py-2 text-xs text-foreground">
        Enumerate the web server on 10.0.0.5.
      </div>
      <div className="max-w-[90%] rounded-2xl rounded-bl-sm border border-border/60 bg-card/70 px-3 py-2 text-xs text-muted-foreground">
        Planned 3 tasks. Proposing{' '}
        <span
          className="text-foreground"
          style={{ fontFamily: 'var(--font-mono)' }}
        >
          nmap -sV -sC 10.0.0.5
        </span>{' '}
        — approve to run.
      </div>
    </div>
  );
}

const TERMINAL_LINES = [
  { text: '$ nmap -sV -sC 10.0.0.5', tone: 'text-foreground' },
  { text: 'PORT    STATE  SERVICE  VERSION', tone: 'text-muted-foreground' },
  { text: '22/tcp  open   ssh      OpenSSH 8.9p1', tone: 'text-term-green' },
  { text: '80/tcp  open   http     nginx 1.18.0', tone: 'text-term-green' },
  { text: '443/tcp open   https    nginx 1.18.0', tone: 'text-term-green' }
];

export function TerminalMock({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-border/60 bg-black/50',
        className
      )}
    >
      <div className="flex items-center gap-2 border-b border-border/60 bg-white/[0.02] px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-redteam/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-primary/50" />
        <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/40" />
        <Mono className="ml-1 text-[10px] text-muted-foreground/80">
          kali :: 10.0.0.5
        </Mono>
      </div>
      <div
        className="space-y-1 px-3 py-3 text-[12px] leading-relaxed"
        style={{ fontFamily: 'var(--font-mono)' }}
      >
        {TERMINAL_LINES.map((line) => (
          <div key={line.text} className={line.tone}>
            {line.text}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ThreatGraphMock({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border/60 bg-card/40 p-3',
        className
      )}
    >
      <Mono className="text-[10px] text-muted-foreground">Operation graph</Mono>
      <svg viewBox="0 0 260 150" className="mt-1 w-full" fill="none">
        <line
          x1="60"
          y1="40"
          x2="130"
          y2="75"
          stroke="hsl(var(--primary)/0.5)"
          strokeWidth="1.5"
        />
        <line
          x1="130"
          y1="75"
          x2="205"
          y2="40"
          stroke="hsl(var(--primary)/0.5)"
          strokeWidth="1.5"
        />
        <line
          x1="130"
          y1="75"
          x2="205"
          y2="112"
          stroke="hsl(var(--primary)/0.5)"
          strokeWidth="1.5"
        />
        <g>
          <circle cx="60" cy="40" r="7" fill="hsl(var(--redteam))" />
          <text
            x="60"
            y="26"
            fill="hsl(var(--muted-foreground))"
            fontSize="9"
            textAnchor="middle"
          >
            Target
          </text>
        </g>
        <g>
          <circle cx="130" cy="75" r="7" fill="hsl(var(--primary))" />
          <text
            x="130"
            y="96"
            fill="hsl(var(--muted-foreground))"
            fontSize="9"
            textAnchor="middle"
          >
            T1190
          </text>
        </g>
        <g>
          <circle cx="205" cy="40" r="7" fill="hsl(var(--primary))" />
          <text
            x="205"
            y="26"
            fill="hsl(var(--muted-foreground))"
            fontSize="9"
            textAnchor="middle"
          >
            nmap
          </text>
        </g>
        <g>
          <circle cx="205" cy="112" r="7" fill="hsl(var(--primary))" />
          <text
            x="205"
            y="132"
            fill="hsl(var(--muted-foreground))"
            fontSize="9"
            textAnchor="middle"
          >
            COA
          </text>
        </g>
      </svg>
    </div>
  );
}
